import { useEffect, useRef, useState } from "preact/hooks";

export type TransitionState = "idle" | "enter" | "leave";

export function useTransitionList<T>(
  list: T[],
  getKey: (item: T) => string | number,
  options?: { durationMs?: number; addOnTop?: boolean }
): Array<T & { state: TransitionState }> {
  const durationMs = options?.durationMs ?? 300;
  const addOnTop = options?.addOnTop ?? true;

  const [annotated, setAnnotated] = useState<
    Array<T & { state: TransitionState }>
  >([]);
  const prevKeysRef = useRef<Set<string | number>>(new Set());

  useEffect(() => {
    const nextKeys = new Set(list.map(getKey));

    const itemsToAdd = list.filter(
      (item) => !prevKeysRef.current.has(getKey(item))
    );

    const leavingKeys = annotated
      .filter((it) => !nextKeys.has(getKey(it)))
      .map((it) => getKey(it));

    setAnnotated((prev) => {
      const withLeaving = prev.map((it) =>
        leavingKeys.includes(getKey(it))
          ? { ...it, state: "leave" as const }
          : it
      );

      const withAdded = addOnTop
        ? [
            ...itemsToAdd.map((it) => ({
              ...it,
              state: "enter" as const,
            })),
            ...withLeaving,
          ]
        : [
            ...withLeaving,
            ...itemsToAdd.map((it) => ({
              ...it,
              state: "enter" as const,
            })),
          ];

      return withAdded;
    });

    const t = setTimeout(() => {
      setAnnotated((prev) =>
        prev
          .filter((it) => it.state !== "leave")
          .map((it) =>
            it.state === "enter" ? { ...it, state: "idle" as const } : it
          )
      );
    }, durationMs);

    prevKeysRef.current = nextKeys;
    return () => clearTimeout(t);
  }, [JSON.stringify(list.map(getKey))]);

  return annotated;
}
